
import { Helmet } from "react-helmet";
import { EnhancedSeoMetadata } from "@/utils/enhancedSeoUtils";

interface EnhancedSeoMetadataProps extends EnhancedSeoMetadata {
  breadcrumbs?: Array<{name: string, url: string}>;
  isArticle?: boolean;
}

const EnhancedSeoMetadataComponent = ({
  title,
  description,
  keywords = [],
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage = "/lainasampo-social-card.jpg",
  articleAuthor,
  articlePublishedTime,
  articleModifiedTime,
  schema,
  breadcrumbs,
  isArticle = false
}: EnhancedSeoMetadataProps) => {
  const siteUrl = "https://lainasampo.fi";
  const keywordsString = Array.isArray(keywords) ? keywords.join(", ") : "";
  
  // MARKKINOINTIOPTIMOIDUT metatiedot
  const marketingOptimizedTitle = title.includes('💰') || title.includes('🏦') ? 
    title : 
    `💰 ${title} | Lainasampo.fi - Säästä rahaa!`;
  
  const marketingOptimizedDescription = description.includes('💰') || description.includes('✅') ?
    description :
    `💰 ${description} ✅ ILMAINEN kilpailutus ✅ Yli 20 pankkia ✅ Säästä jopa 50% korkokuluissa!`;
  
  // Ensure absolute URLs for social sharing
  const absoluteOgImage = ogImage.startsWith('http') 
    ? ogImage 
    : `${siteUrl}${ogImage.startsWith('/') ? '' : '/'}${ogImage}`;
  
  // Generate breadcrumb schema if provided
  const breadcrumbSchema = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": `${siteUrl}${crumb.url}`
    }))
  } : null;
  
  // Detect preview environment to prevent indexing
  const isPreviewEnvironment = 
    typeof window !== 'undefined' && 
    (window.location.hostname.includes('lovable.app') || 
     window.location.hostname.includes('lovable.dev'));

  // ESTÄ VIERASKIELISTEN SIVUJEN INDEKSOINTI GOOGLESSA
  const isLanguagePage = typeof window !== 'undefined' && 
    (window.location.pathname.startsWith('/en') || 
     window.location.pathname.startsWith('/sv') || 
     window.location.pathname.startsWith('/ar') || 
     window.location.pathname.startsWith('/he'));

  // Enhanced schema with image and marketing data
  const enhancedSchema = schema ? {
    ...schema,
    image: schema.image || {
      "@type": "ImageObject",
      "url": absoluteOgImage,
      "width": 1200,
      "height": 630
    },
    // Lisää markkinointidataa schemaan
    brand: {
      "@type": "Brand",
      "name": "Lainasampo.fi",
      "slogan": "Kilpailuta lainasi ja säästä rahaa"
    },
    offers: schema.offers || {
      "@type": "Offer",
      "description": "Ilmainen lainojen kilpailutuspalvelu",
      "price": "0",
      "priceCurrency": "EUR"
    }
  } : null;

  return (
    <Helmet>
      <title>{marketingOptimizedTitle}</title>
      <meta name="description" content={marketingOptimizedDescription} />
      {keywordsString && <meta name="keywords" content={keywordsString} />}
      
      {/* ESTÄ VIERASKIELISTEN SIVUJEN JA PREVIEW-YMPÄRISTÖN INDEKSOINTI */}
      {(isPreviewEnvironment || isLanguagePage) && <meta name="robots" content="noindex, nofollow" />}
      {!isPreviewEnvironment && !isLanguagePage && <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />}
      
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* MARKKINOINTIOPTIMOIDUT Open Graph tags */}
      <meta property="og:type" content={isArticle ? "article" : "website"} />
      <meta property="og:site_name" content="Lainasampo.fi - Suomen edullisin lainavertailu" />
      <meta property="og:title" content={ogTitle || marketingOptimizedTitle} />
      <meta property="og:description" content={ogDescription || marketingOptimizedDescription} />
      <meta property="og:image" content={absoluteOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:alt" content="Lainasampo.fi - Kilpailuta lainasi ja säästä satoja euroja" />
      <meta property="og:locale" content="fi_FI" />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      
      {/* Article specific tags */}
      {isArticle && articleAuthor && (
        <meta property="article:author" content={articleAuthor} />
      )}
      {isArticle && articlePublishedTime && (
        <meta property="article:published_time" content={articlePublishedTime} />
      )}
      {isArticle && articleModifiedTime && (
        <meta property="article:modified_time" content={articleModifiedTime} />
      )}
      
      {/* MARKKINOINTIOPTIMOIDUT Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitle || marketingOptimizedTitle} />
      <meta name="twitter:description" content={ogDescription || marketingOptimizedDescription} />
      <meta name="twitter:image" content={absoluteOgImage} />
      <meta name="twitter:image:alt" content="Lainasampo.fi - Säästä satoja euroja lainassasi!" />
      {canonicalUrl && <meta name="twitter:url" content={canonicalUrl} />}
      
      {/* Performance ja tekninen optimointi */}
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link rel="dns-prefetch" href="https://fonts.googleapis.com" />
      
      {/* Apple touch icon */}
      <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
      <link rel="icon" type="image/png" sizes="180x180" href="/apple-touch-icon.png" />
      
      {/* MARKKINOINTIOPTIMOIDUT structured data - EI vieraskielisille sivuille */}
      {enhancedSchema && !isLanguagePage && (
        <script type="application/ld+json">
          {JSON.stringify(enhancedSchema)}
        </script>
      )}
      
      {/* Breadcrumb schema */}
      {breadcrumbSchema && !isLanguagePage && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      
      {/* MARKKINOINTIOPTIMOIDUT Organization schema - EI vieraskielisille sivuille */}
      {!isLanguagePage && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FinancialService",
            "name": "Lainasampo Oy - Suomen edullisin lainavertailu",
            "alternateName": "Lainasampo.fi",
            "url": "https://lainasampo.fi",
            "logo": {
              "@type": "ImageObject",
              "url": absoluteOgImage,
              "width": 1200,
              "height": 630
            },
            "image": {
              "@type": "ImageObject",
              "url": absoluteOgImage,
              "width": 1200,
              "height": 630
            },
            "description": "Kilpailutamme lainasi ilmaiseksi yli 20 pankissa ja rahoitusyhtiössä. Säästä jopa 50% lainakorkokuluissa.",
            "slogan": "Kilpailuta lainasi ja säästä rahaa",
            "serviceType": "Lainojen kilpailutuspalvelu",
            "areaServed": "Finland",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Mannerheimintie 20 B",
              "addressLocality": "Helsinki",
              "postalCode": "00100",
              "addressCountry": "FI"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+358 9 123 4567",
              "contactType": "customer service",
              "availableLanguage": "Finnish"
            },
            "sameAs": [
              "https://lainasampo.fi"
            ],
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "reviewCount": "1247",
              "bestRating": "5"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Lainatuotteet",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "name": "Kulutusluotto",
                  "description": "Edullinen kulutusluotto ilman vakuuksia",
                  "price": "Alkaen 4.9%",
                  "priceCurrency": "EUR"
                },
                {
                  "@type": "Offer",
                  "name": "Asuntolaina", 
                  "description": "Kilpailukykyinen asuntolaina",
                  "price": "Alkaen 3.2%",
                  "priceCurrency": "EUR"
                }
              ]
            }
          })}
        </script>
      )}
    </Helmet>
  );
};

export default EnhancedSeoMetadataComponent;
